import type {
  StatisticalEngine,
  TeamHistorySample,
  DescriptiveStats,
  Convergence,
  PoissonModel,
  MarketProbability,
  MarketFamily,
  MarketKey,
  Quote,
  OddsComparator,
  NarrativeProvider,
  ProducedAllowedRow,
} from "@tracker-bet-analystic/domain";

export const ENGINE_VERSION = "0.2.0";

const RECENT_CUT_MIN_SELECTED = 10;
const RECENT_CUT_MIN_COVERAGE = 0.8;
const RECENT_CUT_SIZE = 10;
const HISTORY_WINDOW_DAYS = 365;
const POISSON_TAIL_TOLERANCE = 1e-10;
const POISSON_MAX_GOALS = 100;
const CONVERGENCE_MAX_HIGHLIGHTS = 5;
const PROBABILITY_SUM_TOLERANCE = 1e-8;

const CONVERGENCE_LINES = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
const GOAL_LINES = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
const HANDICAP_LINES = [-3, -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 2.5, 3];

const DESCRIPTIVE_ONLY_FAMILIES: readonly MarketFamily[] = [
  "total_corners",
  "total_cards",
  "total_shots",
  "total_shots_on_target",
];

export function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const midVal = sorted[mid];
  const midMinus1Val = sorted[mid - 1];
  if (midVal === undefined || midMinus1Val === undefined) return null;
  return sorted.length % 2 === 0 ? (midMinus1Val + midVal) / 2 : midVal;
}

export function sampleStdDev(values: number[], m: number): number | null {
  if (values.length <= 1) return null;
  const sumSq = values.reduce((acc, v) => acc + (v - m) ** 2, 0);
  return Math.sqrt(sumSq / (values.length - 1));
}

export function isEligible(stats: DescriptiveStats): boolean {
  return (
    stats.selectedCount >= RECENT_CUT_MIN_SELECTED &&
    stats.coverage !== null &&
    stats.coverage >= RECENT_CUT_MIN_COVERAGE
  );
}

/**
 * T05 (§6) — cruzamento produzido × permitido no recorte casa/fora.
 * Somente cálculo: agrega as amostras já selecionadas com as mesmas métricas
 * descritivas do engine. Nenhuma conclusão é criada aqui.
 * O lado "permite" só existe quando o provedor fornece o valor do adversário
 * (coverage 0 / valid 0 quando ausente — a UI deve mostrar "sem dados", nunca 0).
 */
const PRODUCED_ALLOWED_METRICS: ReadonlyArray<{
  key: string;
  label: string;
  unit: string;
  produced: keyof TeamHistorySample;
  allowed: keyof TeamHistorySample;
}> = [
  { key: "goals", label: "Gols", unit: "gols/jogo", produced: "goalsFor", allowed: "goalsAgainst" },
  { key: "shots", label: "Finalizações", unit: "por jogo", produced: "shots", allowed: "shotsAgainst" },
  {
    key: "shotsOnTarget",
    label: "Finalizações no alvo",
    unit: "por jogo",
    produced: "shotsOnTarget",
    allowed: "shotsOnTargetAgainst",
  },
  { key: "corners", label: "Escanteios", unit: "por jogo", produced: "corners", allowed: "cornersAgainst" },
  { key: "xg", label: "xG", unit: "por jogo", produced: "xGFor", allowed: "xGAgainst" },
];

export function buildProducedAllowedMatrix(venue: {
  home: TeamHistorySample[];
  away: TeamHistorySample[];
}): ProducedAllowedRow[] {
  return PRODUCED_ALLOWED_METRICS.map((metric) => ({
    key: metric.key,
    label: metric.label,
    unit: metric.unit,
    homeProduced: calculateDescriptiveStats(venue.home, metric.produced),
    awayAllowed: calculateDescriptiveStats(venue.away, metric.allowed),
    awayProduced: calculateDescriptiveStats(venue.away, metric.produced),
    homeAllowed: calculateDescriptiveStats(venue.home, metric.allowed),
  }));
}

export function calculateDescriptiveStats(
  samples: TeamHistorySample[],
  field: keyof TeamHistorySample
): DescriptiveStats {
  const selectedCount = samples.length;
  const validValues = samples
    .map((s) => s[field])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const validCount = validValues.length;
  const missingCount = selectedCount - validCount;
  const coverage = selectedCount > 0 ? validCount / selectedCount : null;
  const m = mean(validValues);
  const med = median(validValues);
  const std = m !== null ? sampleStdDev(validValues, m) : null;
  return {
    selectedCount,
    validCount,
    missingCount,
    coverage,
    mean: m,
    median: med,
    sampleStdDev: std,
    hits: null,
    hitRate: null,
  };
}

export function calculateHitRate(
  samples: TeamHistorySample[],
  field: keyof TeamHistorySample,
  line: number,
  selection: "over" | "under"
): { hits: number; hitRate: number | null; validCount: number } {
  const validValues = samples
    .map((s) => s[field])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (validValues.length === 0) return { hits: 0, hitRate: null, validCount: 0 };
  const hits = validValues.filter((v) => (selection === "over" ? v > line : v < line)).length;
  return { hits, hitRate: hits / validValues.length, validCount: validValues.length };
}

export function poissonPmf(lambda: number, maxK: number): number[] {
  const pmf: number[] = [];
  let p = Math.exp(-lambda);
  pmf[0] = p;
  for (let k = 1; k <= maxK; k++) {
    p = (p * lambda) / k;
    pmf[k] = p;
  }
  return pmf;
}

export function buildPoissonModel(
  homeGoalsFor: number[],
  homeGoalsAgainst: number[],
  awayGoalsFor: number[],
  awayGoalsAgainst: number[]
): PoissonModel | null {
  const homeGoalsForMean = mean(homeGoalsFor) ?? 0;
  const homeGoalsAgainstMean = mean(homeGoalsAgainst) ?? 0;
  const awayGoalsForMean = mean(awayGoalsFor) ?? 0;
  const awayGoalsAgainstMean = mean(awayGoalsAgainst) ?? 0;

  const lambdaHome = (homeGoalsForMean + awayGoalsAgainstMean) / 2;
  const lambdaAway = (awayGoalsForMean + homeGoalsAgainstMean) / 2;

  if (!Number.isFinite(lambdaHome) || !Number.isFinite(lambdaAway) || lambdaHome < 0 || lambdaAway < 0) {
    return null;
  }

  const homePmf = poissonPmf(lambdaHome, POISSON_MAX_GOALS);
  const awayPmf = poissonPmf(lambdaAway, POISSON_MAX_GOALS);

  const matrix: number[][] = Array.from({ length: POISSON_MAX_GOALS + 1 }, () =>
    Array.from({ length: POISSON_MAX_GOALS + 1 }, () => 0)
  );

  let representedMass = 0;
  for (let h = 0; h <= POISSON_MAX_GOALS; h++) {
    const hp = homePmf[h];
    if (hp === undefined) continue;
    const row = matrix[h];
    if (!row) continue;
    for (let a = 0; a <= POISSON_MAX_GOALS; a++) {
      const ap = awayPmf[a];
      if (ap === undefined) continue;
      const prob = hp * ap;
      row[a] = prob;
      representedMass += prob;
    }
  }

  const residualMass = 1 - representedMass;
  if (Math.abs(residualMass) > POISSON_TAIL_TOLERANCE) {
    return null;
  }

  const limitations = [
    "Independência entre gols do mandante e visitante",
    "Pequena amostra (máx. 10 jogos por recorte)",
    "Ausência de ajuste por força da liga/equipe",
    "Sobredispersão não modelada (Poisson assume variância = média)",
    `Massa residual da matriz truncada: ${residualMass.toExponential(4)}`,
  ];

  return {
    type: "poisson-independent-v0.1",
    lambdaHome,
    lambdaAway,
    residualMass,
    maxGoals: POISSON_MAX_GOALS,
    matrix,
    limitations,
  };
}

export function extractProbabilitiesFromMatrix(
  matrix: number[][],
  marketKey: MarketKey
): { pWin: number; pPush: number; pLoss: number } | null {
  const { family, selection, line } = marketKey;
  let pWin = 0;
  let pPush = 0;
  let pLoss = 0;

  for (let h = 0; h < matrix.length; h++) {
    const row = matrix[h];
    if (!row) continue;
    for (let a = 0; a < row.length; a++) {
      const prob = row[a];
      if (prob === undefined || prob === 0) continue;

      let outcome: "win" | "push" | "loss";
      if (family === "result_1x2") {
        if (selection === "home") outcome = h > a ? "win" : "loss";
        else if (selection === "away") outcome = a > h ? "win" : "loss";
        else outcome = h === a ? "win" : "loss";
      } else if (family === "total_goals" && typeof line === "number") {
        const total = h + a;
        if (selection === "over") outcome = total > line ? "win" : total === line ? "push" : "loss";
        else outcome = total < line ? "win" : total === line ? "push" : "loss";
      } else if (family === "both_teams_to_score") {
        const both = h > 0 && a > 0;
        if (selection === "yes") outcome = both ? "win" : "loss";
        else outcome = both ? "loss" : "win";
      } else if (family === "handicap" && typeof line === "number") {
        const margin = selection === "home" ? h - a : a - h;
        const adjusted = margin + line;
        outcome = adjusted > 0 ? "win" : adjusted === 0 ? "push" : "loss";
      } else {
        return null;
      }

      if (outcome === "win") pWin += prob;
      else if (outcome === "push") pPush += prob;
      else pLoss += prob;
    }
  }

  if (Math.abs(pWin + pPush + pLoss - 1) > PROBABILITY_SUM_TOLERANCE) return null;

  return { pWin, pPush, pLoss };
}

export function marketKeyToString(key: MarketKey): string {
  const parts = [
    key.family,
    key.period,
    key.subjectId,
    key.selection,
    key.line?.toString() ?? "",
    key.settlementRule,
  ];
  return parts.filter(Boolean).join("|");
}

function comparableQuotes(marketKey: MarketKey, quotes: Quote[]): Quote[] {
  const key = marketKeyToString(marketKey);
  return quotes.filter(
    (q) =>
      marketKeyToString(q.marketKey) === key &&
      Number.isFinite(q.decimalOdds) &&
      q.decimalOdds > 1
  );
}

function bestQuoteOf(quotes: Quote[]): Quote | null {
  return quotes.reduce<Quote | null>(
    (best, q) => (best === null || q.decimalOdds > best.decimalOdds ? q : best),
    null
  );
}

export function evaluateMarket(
  marketKey: MarketKey,
  model: PoissonModel | null,
  quotes: Quote[]
): MarketProbability {
  if (!model) {
    return {
      marketKey,
      status: "insufficient_data",
      pWin: null,
      pPush: null,
      pLoss: null,
      fairOdds: null,
      ev: null,
      reason:
        "Amostra insuficiente: recortes de gols não atendem elegibilidade (mínimo 10 jogos e cobertura ≥ 80%)",
    };
  }

  const probs = extractProbabilitiesFromMatrix(model.matrix, marketKey);
  if (!probs) {
    const descriptiveOnly = DESCRIPTIVE_ONLY_FAMILIES.includes(marketKey.family);
    return {
      marketKey,
      status: "unsupported_market",
      pWin: null,
      pPush: null,
      pLoss: null,
      fairOdds: null,
      ev: null,
      reason: descriptiveOnly
        ? "Estatística descritiva sem modelo preditivo nesta versão; fairOdds e EV não são calculados"
        : "Mercado não suportado pelo modelo experimental nesta versão",
    };
  }

  const { pWin, pPush, pLoss } = probs;
  const fairOdds = pWin > 0 ? 1 + pLoss / pWin : null;

  const bestQuote = bestQuoteOf(comparableQuotes(marketKey, quotes));
  if (!bestQuote) {
    return {
      marketKey,
      status: "awaiting_odds",
      pWin,
      pPush,
      pLoss,
      fairOdds,
      ev: null,
      reason: "Sem odds vigentes para este mercado nas casas ativas",
    };
  }

  const ev = pWin * (bestQuote.decimalOdds - 1) - pLoss;
  const status = ev > 0 ? "experimental_value" : "no_value";

  return {
    marketKey,
    status,
    pWin,
    pPush,
    pLoss,
    fairOdds,
    ev,
    reason:
      status === "experimental_value"
        ? `Valor experimental: EV=${ev.toFixed(4)} com odd ${bestQuote.decimalOdds.toFixed(2)} (${bestQuote.bookmakerName})`
        : `Sem valor na odd atual: EV=${ev.toFixed(4)} com odd ${bestQuote.decimalOdds.toFixed(2)} (${bestQuote.bookmakerName})`,
  };
}

export function selectSamples(
  allSamples: TeamHistorySample[],
  cutoffAt: string,
  fixtureId: string,
  isHome: boolean | null,
  maxCount: number,
  maxAgeDays: number
): TeamHistorySample[] {
  const cutoffMs = new Date(cutoffAt).getTime();
  const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;

  const eligible = allSamples.filter((s) => {
    const matchMs = new Date(s.date).getTime();
    return (
      matchMs < cutoffMs &&
      cutoffMs - matchMs <= maxAgeMs &&
      s.fixtureId !== fixtureId &&
      (isHome === null || s.isHome === isHome)
    );
  });

  eligible.sort((a, b) => {
    const diff = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (diff !== 0) return diff;
    return a.fixtureId.localeCompare(b.fixtureId);
  });

  const seen = new Set<string>();
  const deduped = eligible.filter((s) => {
    if (seen.has(s.fixtureId)) return false;
    seen.add(s.fixtureId);
    return true;
  });

  return deduped.slice(0, maxCount);
}

export function selectSeasonSamples(
  allSamples: TeamHistorySample[],
  cutoffAt: string,
  fixtureId: string,
  isHome: boolean | null,
  seasonStart: string
): TeamHistorySample[] {
  const cutoffMs = new Date(cutoffAt).getTime();
  const seasonStartMs = new Date(seasonStart).getTime();

  const eligible = allSamples.filter((s) => {
    const matchMs = new Date(s.date).getTime();
    return (
      matchMs < cutoffMs &&
      matchMs >= seasonStartMs &&
      s.fixtureId !== fixtureId &&
      (isHome === null || s.isHome === isHome)
    );
  });

  eligible.sort((a, b) => {
    const diff = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (diff !== 0) return diff;
    return a.fixtureId.localeCompare(b.fixtureId);
  });

  const seen = new Set<string>();
  return eligible.filter((s) => {
    if (seen.has(s.fixtureId)) return false;
    seen.add(s.fixtureId);
    return true;
  });
}

export const demoStatisticalEngine: StatisticalEngine = {
  async analyze(input) {
    const { fixture, cutoffAt, samples: pools } = input;

    const samples = {
      general: {
        home: selectSamples(pools.general.home, cutoffAt, fixture.id, null, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
        away: selectSamples(pools.general.away, cutoffAt, fixture.id, null, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
      },
      venue: {
        home: selectSamples(pools.venue.home, cutoffAt, fixture.id, true, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
        away: selectSamples(pools.venue.away, cutoffAt, fixture.id, false, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
      },
      season: {
        home: selectSeasonSamples(pools.season.home, cutoffAt, fixture.id, true, `${fixture.season}-01-01`),
        away: selectSeasonSamples(pools.season.away, cutoffAt, fixture.id, false, `${fixture.season}-01-01`),
      },
    };

    const metrics: Array<keyof TeamHistorySample> = [
      "goalsFor",
      "goalsAgainst",
      "shots",
      "shotsOnTarget",
      "corners",
      "yellowCards",
      "redCards",
      "possession",
      "passes",
      "xGFor",
      "xGAgainst",
    ];

    const qualityByMetric: Record<string, { home: DescriptiveStats; away: DescriptiveStats }> = {};
    const descriptiveStats: Record<string, { home: DescriptiveStats; away: DescriptiveStats }> = {};

    for (const metric of metrics) {
      const home = calculateDescriptiveStats(samples.venue.home, metric);
      const away = calculateDescriptiveStats(samples.venue.away, metric);
      qualityByMetric[metric] = { home, away };
      descriptiveStats[metric] = { home, away };
    }

    const homeProducedStats = calculateDescriptiveStats(samples.venue.home, "goalsFor");
    const awayAllowedStats = calculateDescriptiveStats(samples.venue.away, "goalsAgainst");

    let convergence: Convergence[] = [];
    if (isEligible(homeProducedStats) && isEligible(awayAllowedStats)) {
      convergence = CONVERGENCE_LINES.map((line) => {
        const homeOver = calculateHitRate(samples.venue.home, "goalsFor", line, "over");
        const awayOver = calculateHitRate(samples.venue.away, "goalsAgainst", line, "over");
        const homeFrequency = homeOver.hitRate ?? 0;
        const awayFrequency = awayOver.hitRate ?? 0;
        const level: Convergence["level"] =
          homeFrequency >= 0.7 && awayFrequency >= 0.7 && Math.abs(homeFrequency - awayFrequency) <= 0.2
            ? "high"
            : homeFrequency >= 0.6 && awayFrequency >= 0.6
              ? "moderate"
              : "low";
        return {
          marketKey: {
            family: "total_goals",
            period: "FT90",
            subjectId: "match",
            selection: "over",
            line,
            settlementRule: "no_extra_time",
          },
          homeFrequency,
          awayFrequency,
          level,
          homeSample: { selected: homeProducedStats.selectedCount, valid: homeProducedStats.validCount },
          awaySample: { selected: awayAllowedStats.selectedCount, valid: awayAllowedStats.validCount },
        };
      });

      const levelRank: Record<Convergence["level"], number> = { high: 0, moderate: 1, low: 2 };
      convergence.sort((a, b) => {
        const byLevel = levelRank[a.level] - levelRank[b.level];
        if (byLevel !== 0) return byLevel;
        const minValidA = Math.min(a.homeSample.valid, a.awaySample.valid);
        const minValidB = Math.min(b.homeSample.valid, b.awaySample.valid);
        if (minValidA !== minValidB) return minValidB - minValidA;
        const coverageA = Math.min(
          a.homeSample.valid / a.homeSample.selected,
          a.awaySample.valid / a.awaySample.selected
        );
        const coverageB = Math.min(
          b.homeSample.valid / b.homeSample.selected,
          b.awaySample.valid / b.awaySample.selected
        );
        if (coverageA !== coverageB) return coverageB - coverageA;
        return marketKeyToString(a.marketKey).localeCompare(marketKeyToString(b.marketKey));
      });
      convergence = convergence.slice(0, CONVERGENCE_MAX_HIGHLIGHTS);
    }

    const goalsStats = {
      homeFor: calculateDescriptiveStats(samples.venue.home, "goalsFor"),
      homeAgainst: calculateDescriptiveStats(samples.venue.home, "goalsAgainst"),
      awayFor: calculateDescriptiveStats(samples.venue.away, "goalsFor"),
      awayAgainst: calculateDescriptiveStats(samples.venue.away, "goalsAgainst"),
    };

    let model: PoissonModel | null = null;
    if (
      isEligible(goalsStats.homeFor) &&
      isEligible(goalsStats.homeAgainst) &&
      isEligible(goalsStats.awayFor) &&
      isEligible(goalsStats.awayAgainst)
    ) {
      const toNumbers = (values: Array<number | null>): number[] =>
        values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
      model = buildPoissonModel(
        toNumbers(samples.venue.home.map((s) => s.goalsFor)),
        toNumbers(samples.venue.home.map((s) => s.goalsAgainst)),
        toNumbers(samples.venue.away.map((s) => s.goalsFor)),
        toNumbers(samples.venue.away.map((s) => s.goalsAgainst))
      );
    }

    const marketKeys: MarketKey[] = [
      { family: "result_1x2", period: "FT90", subjectId: "match", selection: "home", settlementRule: "no_extra_time" },
      { family: "result_1x2", period: "FT90", subjectId: "match", selection: "draw", settlementRule: "no_extra_time" },
      { family: "result_1x2", period: "FT90", subjectId: "match", selection: "away", settlementRule: "no_extra_time" },
      { family: "both_teams_to_score", period: "FT90", subjectId: "match", selection: "yes", settlementRule: "no_extra_time" },
      { family: "both_teams_to_score", period: "FT90", subjectId: "match", selection: "no", settlementRule: "no_extra_time" },
      ...GOAL_LINES.map((line) => ({
        family: "total_goals" as const,
        period: "FT90" as const,
        subjectId: "match",
        selection: "over" as const,
        line,
        settlementRule: "no_extra_time" as const,
      })),
      ...GOAL_LINES.map((line) => ({
        family: "total_goals" as const,
        period: "FT90" as const,
        subjectId: "match",
        selection: "under" as const,
        line,
        settlementRule: "no_extra_time" as const,
      })),
      ...HANDICAP_LINES.map((line) => ({
        family: "handicap" as const,
        period: "FT90" as const,
        subjectId: "match",
        selection: "home" as const,
        line,
        settlementRule: "no_extra_time" as const,
      })),
      { family: "total_corners", period: "FT90", subjectId: "match", selection: "over", line: 8.5, settlementRule: "no_extra_time" },
      { family: "total_cards", period: "FT90", subjectId: "match", selection: "over", line: 3.5, settlementRule: "no_extra_time" },
      { family: "total_shots", period: "FT90", subjectId: "match", selection: "over", line: 24.5, settlementRule: "no_extra_time" },
      { family: "total_shots_on_target", period: "FT90", subjectId: "match", selection: "over", line: 8.5, settlementRule: "no_extra_time" },
    ];

    const markets = marketKeys.map((mk) => evaluateMarket(mk, model, []));

    return {
      qualityByMetric,
      descriptiveStats,
      convergence,
      model,
      markets,
    };
  },
};

export const demoOddsComparator: OddsComparator = {
  compare(input) {
    const { marketKey, quotes, outcomeProbabilities } = input;
    const { pWin, pLoss } = outcomeProbabilities;

    const comparable = comparableQuotes(marketKey, quotes);
    const bestQuote = bestQuoteOf(comparable);
    const tiedQuotes = bestQuote
      ? comparable.filter((q) => q.decimalOdds === bestQuote.decimalOdds)
      : [];

    const fairOdds = pWin > 0 ? 1 + pLoss / pWin : null;
    const ev = bestQuote ? pWin * (bestQuote.decimalOdds - 1) - pLoss : null;
    const status = bestQuote ? (ev !== null && ev > 0 ? "experimental_value" : "no_value") : "awaiting_odds";

    return { bestQuote, tiedQuotes, fairOdds, ev, status };
  },
};

export const demoNarrativeProvider: NarrativeProvider = {
  async explain(snapshot) {
    const { fixture, model, markets, quotes, descriptiveStats, convergence } = snapshot;
    const hasQuotes = quotes.length > 0;

    const lines: string[] = [];
    lines.push(`**Análise: ${fixture.homeTeamName} vs ${fixture.awayTeamName}**`);
    lines.push("");
    lines.push(
      `*Competição: ${fixture.competitionName} | Data: ${new Date(fixture.kickoffAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}*`
    );
    lines.push("");

    if (snapshot.dataMode === "demo") {
      lines.push("> ⚠️ **MODO DEMONSTRAÇÃO** — Dados sintéticos para ilustração da interface. Não use para decisões reais.");
      lines.push("");
    }

    lines.push(`**Corte temporal:** ${new Date(snapshot.cutoffAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}`);
    lines.push(`**Versão do motor:** ${snapshot.engineVersion}`);
    lines.push("");

    if (model) {
      lines.push(`**Modelo experimental:** ${model.type} (não calibrado)`);
      lines.push(`λ mandante: ${model.lambdaHome.toFixed(2)} | λ visitante: ${model.lambdaAway.toFixed(2)}`);
      lines.push(`Limitações: ${model.limitations.join("; ")}`);
    } else {
      lines.push("**Modelo:** Indisponível — recortes de gols não atendem elegibilidade (mínimo 10 jogos e cobertura ≥ 80%).");
    }
    lines.push("");

    const gfHome = descriptiveStats.goalsFor?.home;
    const gaHome = descriptiveStats.goalsAgainst?.home;
    const gfAway = descriptiveStats.goalsFor?.away;
    const gaAway = descriptiveStats.goalsAgainst?.away;
    lines.push("**Estatísticas descritivas (condição casa/fora, últimos 10):**");
    if (gfHome && gaHome && gfAway && gaAway) {
      lines.push(
        `- ${fixture.homeTeamName} (casa): GF média ${gfHome.mean?.toFixed(2) ?? "—"}, GC média ${gaHome.mean?.toFixed(2) ?? "—"} (cobertura ${((gfHome.coverage ?? 0) * 100).toFixed(0)}%)`
      );
      lines.push(
        `- ${fixture.awayTeamName} (fora): GF média ${gfAway.mean?.toFixed(2) ?? "—"}, GC média ${gaAway.mean?.toFixed(2) ?? "—"} (cobertura ${((gfAway.coverage ?? 0) * 100).toFixed(0)}%)`
      );
    }
    lines.push("");

    if (convergence.length > 0) {
      lines.push("**Convergência descritiva (frequência produzida vs permitida, linhas .5):**");
      for (const c of convergence) {
        lines.push(
          `- Over ${c.marketKey.line}: mandante ${(c.homeFrequency * 100).toFixed(0)}%, visitante ${(c.awayFrequency * 100).toFixed(0)}% → ${c.level}`
        );
      }
      lines.push("");
    }

    const statusLabels: Record<string, string> = {
      experimental_value: "Valor experimental (modelo não calibrado)",
      no_value: "Sem valor na odd atual",
      awaiting_odds: "Aguardando odds",
      insufficient_data: "Dados insuficientes",
      unsupported_market: "Mercado não suportado",
    };

    lines.push("**Mercados avaliados:**");
    for (const m of markets) {
      const line = m.marketKey.line !== undefined ? ` ${m.marketKey.line}` : "";
      const label = `${m.marketKey.family} ${m.marketKey.selection}${line}`;
      let row = `- ${label}: ${statusLabels[m.status] ?? m.status}`;
      if (m.fairOdds !== null) row += ` | Fair odd: ${m.fairOdds.toFixed(2)}`;
      if (m.ev !== null) row += ` | EV: ${(m.ev * 100).toFixed(1)}%`;
      lines.push(row);
    }

    if (!hasQuotes) {
      lines.push("");
      lines.push("*Configure odds nas suas casas para ver comparação e EV.*");
    }

    lines.push("");
    lines.push("---");
    lines.push(
      "*Esta análise é experimental. Frequência histórica não é probabilidade preditiva calibrada. Odds justas e EV derivam de modelo Poisson independente não calibrado e não constituem recomendação.*"
    );

    return {
      text: lines.join("\n"),
      usage: { promptTokens: 0, completionTokens: 0 },
    };
  },
};
